/**
 * src/api/printing/logging.ts — Wrapper isolé pour log_print_attempt.
 *
 * SEUL fichier autorisé à appeler supabase pour le logging d'impression.
 * Invariant 4.B.2 : toute interaction supabase pour print-logging passe ici.
 * Invariant 4.A.15 : aucun PII loggé (pas de client_name, email, IBAN).
 *
 * Migration : src/services/print/printLog.ts est maintenu en shim qui délègue ici.
 * Ce module est la source de vérité — printLog.ts sera supprimé en Phase D.
 */

import { supabase } from '@/integrations/supabase/client';

// ---------------------------------------------------------------------------
// Types de log
// ---------------------------------------------------------------------------

/**
 * Route physique empruntée par l'impression.
 * Doit correspondre aux valeurs acceptées par la RPC `log_print_attempt`.
 */
export type PrintLogRoute =
  | 'aidl'
  | 'cloud_printer'
  | 'bluetooth'
  | 'network'
  | 'sunmi_legacy'
  | 'fallback'
  | 'unknown';

/**
 * Type de document imprimé.
 * Doit correspondre aux valeurs acceptées par la RPC `log_print_attempt`.
 */
export type PrintLogDocType =
  | 'receipt'
  | 'expense'
  | 'z_report'
  | 'x_report'
  | 'kitchen'
  | 'test'
  | 'drawer'
  | 'other';

export type LogPrintParams = {
  documentType: PrintLogDocType;
  route: PrintLogRoute;
  success: boolean;
  registerId?: string | null;
  printerName?: string | null;
  printerId?: string | null;
  printerMac?: string | null;
  /** Message d'erreur technique — JAMAIS de PII (invariant 4.A.15). */
  errorMessage?: string | null;
  payloadSizeBytes?: number | null;
  durationMs?: number | null;
  /** Métadonnées libres — JAMAIS de PII (invariant 4.A.15). */
  metadata?: Record<string, unknown> | null;
};

// ---------------------------------------------------------------------------
// Helpers internes
// ---------------------------------------------------------------------------

/** Détection plateforme : préfixe android/web + version. */
function getClientAppVersion(): string {
  // window n'est pas disponible en SSR/tests — guard explicite
  if (typeof window === 'undefined') return 'unknown';
  const w = window as typeof window & Record<string, unknown>;
  const capacitor = w['Capacitor'] as Record<string, unknown> | undefined;
  const isNative =
    typeof capacitor?.['isNativePlatform'] === 'function' &&
    (capacitor['isNativePlatform'] as () => boolean)() === true;
  const platform = isNative ? 'android' : 'web';
  const appVersionGlobal =
    typeof (globalThis as Record<string, unknown>)['__APP_VERSION__'] === 'string'
      ? (globalThis as Record<string, unknown>)['__APP_VERSION__']
      : null;
  const version =
    (appVersionGlobal as string | null) ??
    (w['__VISIONPOS_APP_VERSION'] as string | undefined) ??
    'unknown';
  return `${platform}-${version}`;
}

function getDeviceId(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem('visionpos.device.id');
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Fonction publique
// ---------------------------------------------------------------------------

/**
 * Fire-and-forget — n'échoue jamais la transaction appelante.
 *
 * Seul point d'accès supabase pour le logging impression.
 * Invariant 4.A.15 : s'assure que errorMessage ne contient pas de PII
 * (responsabilité de l'appelant — ce wrapper ne peut pas le vérifier
 * structurellement, mais le convention est documentée).
 */
export async function logPrintAttempt(p: LogPrintParams): Promise<void> {
  try {
    await supabase.rpc('log_print_attempt', {
      p_document_type: p.documentType,
      p_route: p.route,
      p_success: p.success,
      p_register_id: p.registerId ?? null,
      p_device_id: getDeviceId(),
      p_printer_name: p.printerName ?? null,
      p_printer_id: p.printerId ?? null,
      p_printer_mac: p.printerMac ?? null,
      p_error_message: p.errorMessage ?? null,
      p_payload_size_bytes: p.payloadSizeBytes ?? null,
      p_duration_ms: p.durationMs ?? null,
      p_client_app_version: getClientAppVersion(),
      p_metadata: (p.metadata ?? null) as {
        [key: string]: import('@/integrations/supabase/types').Json;
      } | null,
    });
  } catch (e) {
    // Logging ne doit jamais bloquer le flux principal.
    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
      console.warn('[printing/logging] log_print_attempt failed:', e);
    }
  }
}
